import { Injectable } from '@angular/core';

import { Store } from '@ngrx/store';
import { merge, NEVER, of, tap } from 'rxjs';
import { catchError, filter, switchMap } from 'rxjs/operators';

import { AccountSelectors, AdminSocket } from '@recruitee/account';
import { CurrentUserEntityActions } from '@recruitee/account-data';
import { PageVisibilityService } from '@recruitee/common';
import { bufferUntil, fromChannel } from '@recruitee/websocket';

import { AbilitiesActions } from './abilities.store';

@Injectable({
  providedIn: 'root',
})
export class AbilitiesWebsocketService {
  constructor(
    private adminSocket: AdminSocket,
    private store: Store,
    private pageVisibilityService: PageVisibilityService,
  ) {}

  public init(): void {
    this.store
      .select(AccountSelectors.currentCompanyWithUser)
      .pipe(filter(({ currentCompany, currentUser }) => !!currentUser && !!currentCompany))
      .subscribe(({ currentCompany, currentUser }) =>
        this.subscribeToWsMessages(currentCompany!.id, currentUser.id),
      );
  }

  private subscribeToWsMessages(companyId: number, userId: number) {
    const pageVisibleTrigger = () =>
      this.pageVisibilityService.isPageVisible()
        ? of(null)
        : this.pageVisibilityService.$onPageVisible;

    this.adminSocket.ready$
      .pipe(
        switchMap(() => this.adminSocket.subscribeToChannel(`admin:${companyId}:${userId}`)),
        catchError(err => {
          console.error(err);
          return NEVER;
        }),
        switchMap(channel =>
          merge(
            fromChannel(channel, 'role-updated').pipe(
              bufferUntil(pageVisibleTrigger),
              tap(() => {
                this.store.dispatch(CurrentUserEntityActions.fetch());
                this.store.dispatch(AbilitiesActions.roleUpdated());
              }),
            ),
            fromChannel(channel, 'membership-updated').pipe(
              bufferUntil(pageVisibleTrigger),
              tap(() => {
                this.store.dispatch(CurrentUserEntityActions.fetch());
                this.store.dispatch(AbilitiesActions.membershipUpdated());
              }),
            ),
          ),
        ),
      )
      .subscribe();
  }
}
